/** Turning a failed request into something safe to put on screen.
 *
 *  The rule: NEVER render the server's response body. Not the `detail` string, not a
 *  validation array, not an exception message. Two reasons, and the second is the one
 *  that bites.
 *
 *  1. PHI. Several handlers put real content in a message — "Patient not found",
 *     "Confirm Lab Draw request → Thursday, Nov 6 at 14:00", a 409 naming a provider
 *     and a time. Echoing a server string into a banner is an uncontrolled channel out
 *     of the building: into a screenshot pasted in a ticket, a browser console, a
 *     support email, an error aggregator. The audit chain records who read what; a
 *     banner records nothing.
 *
 *  2. It leaks the shape of the system. An unsigned-BAA 403 names the vendor; a 404 on
 *     a cross-tenant id is deliberately indistinguishable from a missing row, and
 *     pasting the server's words next to it can undo that on its own.
 *
 *  So this maps STATUS to fixed copy written here. The mapping is closed: an
 *  unrecognised status gets the generic sentence, never the body.
 *
 *  Deliberately NOT a blanket ban across the app. A few places have bespoke, carefully
 *  written server copy that is the right thing to show — the booking 409 hands back
 *  "That time was just taken" with alternatives, and `formatApiError` still exists for
 *  those. The distinction is author-written copy a screen deliberately renders, versus
 *  a generic banner dumping whatever came back. This function is for the second case.
 */

export const GENERIC_ERROR = "Something went wrong. Please try again.";

const BY_STATUS = {
  400: "That request wasn't valid. Please check what you entered and try again.",
  401: "Your session has ended. Please sign in again.",
  403: "You don't have access to this.",
  404: "We couldn't find that.",
  409: "Someone else changed this first. Reload and try again.",
  422: "That request wasn't valid. Please check what you entered and try again.",
  429: "Too many requests just now. Wait a moment and try again.",
  500: "The server had a problem. Nothing was saved.",
  502: "The server is unreachable right now.",
  503: "That feature isn't available right now.",
  504: "The server took too long to respond.",
};

/** A sentence that is safe to display for a failed request.
 *
 *  Takes the axios error (or anything with `response.status`). Never reads
 *  `response.data`.
 */
export function safeErrorMessage(error) {
  const status = error?.response?.status;

  // No response at all: the request never completed. Worth distinguishing, because
  // "check your connection" is actionable and the generic sentence is not.
  if (!status) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (BY_STATUS[status]) return BY_STATUS[status];
  if (status >= 500) return BY_STATUS[500];
  if (status >= 400) return GENERIC_ERROR;
  return GENERIC_ERROR;
}

/** True when the failure means the session is gone, so a retry button is pointless —
 *  the app is about to redirect to a login screen and a "Try again" would just 401
 *  again. Callers use this to show the message without the button. */
export function isSessionGone(error) {
  return error?.response?.status === 401;
}
